import React, { useState, useEffect, useContext } from 'react';
import { Modal, Form, Input, Button, message, Steps, Row, Col } from 'antd';
import { MailOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { sendVerificationCode, verifyCode, resetPassword, clearError, clearAuthFlags } from '../../redux/slices/authSlice';
import logger from '../../utils/logger';
import './ResetPasswordModal.css';
import { GlobalMessageApiContext } from '../../App';
import languageManager from '../../utils/languageManager';

const { Step } = Steps;

const ResetPasswordModal = ({ visible, onClose, onShowLogin }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [email, setEmail] = useState('');
  const messageApi = useContext(GlobalMessageApiContext);

  const dispatch = useDispatch();
  const { loading, error, codeSent, codeVerified, passwordReset } = useSelector(state => state.auth);
  
  // 重置状态
  useEffect(() => {
    if (!visible) {
      dispatch(clearAuthFlags());
    }
  }, [visible, dispatch]);
  
  // 处理错误消息
  useEffect(() => {
    if (error) {
      messageApi.open({
        type: 'error',
        content: error,
      });
      dispatch(clearError());
    }
  }, [error, dispatch, messageApi]);

  // 密码重置成功后处理
  useEffect(() => {
    if (passwordReset) {
      messageApi.open({
        type: 'success',
        content: languageManager.t('auth.resetPasswordSuccess'),
      });
      form.resetFields();
      setCurrentStep(0);
      onShowLogin();
      dispatch(clearAuthFlags());
    }
  }, [passwordReset, form, onShowLogin, dispatch, messageApi]);

  // 验证码验证成功后处理
  useEffect(() => {
    if (codeVerified && currentStep === 0) {
      setCurrentStep(1);
    }
  }, [codeVerified, currentStep]);
  
  // 处理倒计时
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);
  
  // 处理发送验证码
  const handleSendVerificationCode = async () => {
    try {
      // 验证邮箱
      await form.validateFields(['email']);
      const emailValue = form.getFieldValue('email');
      setEmail(emailValue);
      
      try {
        // 发送验证码
        await dispatch(sendVerificationCode(emailValue)).unwrap();
        
        // 发送成功，设置倒计时
        setCountdown(60);
        messageApi.open({
          type: 'success',
          content: `${languageManager.t('auth.sendToEmail')} ${emailValue}`,
        });
      } catch (error) {
        // 显式处理发送验证码失败
        logger.error('发送验证码失败:', error);
        if (error.message) {
          messageApi.open({
            type: 'error',
            content: error.message,
          });
        } else {
          messageApi.open({
            type: 'error',
            content: languageManager.t('auth.resetPasswordFailed'),
          });
        }
      }
    } catch (error) {
      // 表单验证错误
      logger.error('表单验证失败:', error);
    }
  };
  
  // 下一步
  const nextStep = async () => {
    try {
      if (currentStep === 0) {
        // 验证邮箱和验证码
        await form.validateFields(['email', 'verificationCode']);
        const emailValue = form.getFieldValue('email');
        const code = form.getFieldValue('verificationCode');
        
        try {
          // 验证验证码
          await dispatch(verifyCode({ email: emailValue, code })).unwrap();
          // 验证成功的处理由useEffect监听codeVerified状态变化来完成
        } catch (error) {
          // 显式处理验证码验证失败
          logger.error('[ResetPasswordModal] 验证码验证失败:', error);
          if (error.message) {
            messageApi.open({
              type: 'error',
              content: error.message,
            });
          } else {
            messageApi.open({
              type: 'error',
              content: languageManager.t('auth.verifyCodeFailed'),
            });
          }
          return; // 阻止继续执行
        }
      } else if (currentStep === 1) {
        // 验证密码
        await form.validateFields(['password', 'confirmPassword']);
        const password = form.getFieldValue('password');
        const code = form.getFieldValue('verificationCode');
        
        try {
          // 重置密码
          await dispatch(resetPassword({ email, code, password })).unwrap();
          // 成功处理由useEffect监听passwordReset状态变化来完成
        } catch (error) {
          // 显式处理密码重置失败
          logger.error('[ResetPasswordModal] 密码重置失败:', error);
          if (error.message) {
            messageApi.open({
              type: 'error',
              content: error.message,
            });
          } else {
            messageApi.open({
              type: 'error',
              content: languageManager.t('auth.resetPasswordFailed'),
            });
          }
          return; // 阻止继续执行
        }
      }
    } catch (error) {
      logger.error('[ResetPasswordModal] 表单验证失败:', error);
    }
  };
  
  // 返回上一步
  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };
  
  // 返回登录页面
  const handleGoToLogin = () => {
    form.resetFields();
    setCurrentStep(0);
    dispatch(clearAuthFlags());
    onShowLogin();
  };
  
  // 关闭弹窗时重置步骤
  const handleClose = () => {
    form.resetFields();
    setCurrentStep(0);
    dispatch(clearAuthFlags());
    onClose();
  };

  // 步骤1：输入邮箱和验证码
  const renderStepOne = () => (
    <>
      <Form.Item
        name="email"
        rules={[
          { required: true, message: languageManager.t('auth.enterEmail') },
          { type: 'email', message: languageManager.t('auth.enterValidEmail') }
        ]}
      >
        <Input 
          prefix={<MailOutlined />} 
          placeholder={languageManager.t('auth.email')} 
          size="large"
          autoComplete="email"
        />
      </Form.Item>
      
      <Form.Item
        name="verificationCode"
        rules={[
          { required: true, message: languageManager.t('auth.enterVerificationCode') },
          { len: 6, message: languageManager.t('auth.codeShouldBeSixDigits') }
        ]}
      >
        <Row gutter={8}>
          <Col span={16}>
            <Input
              prefix={<SafetyOutlined />}
              placeholder={languageManager.t('auth.verificationCode')}
              size="large"
            />
          </Col>
          <Col span={8}>
            <Button
              size="large"
              className="verify-code-button"
              onClick={handleSendVerificationCode}
              disabled={countdown > 0 || loading}
              loading={loading && countdown === 0}
              block
            >
              {countdown > 0 ? `${countdown}${languageManager.t('auth.resendAfter')}` : languageManager.t('auth.getVerificationCode')}
            </Button>
          </Col>
        </Row>
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          size="large"
          block
          onClick={nextStep}
          loading={loading}
        >
          {languageManager.t('auth.nextStep')}
        </Button>
      </Form.Item>
    </>
  );

  // 步骤2：设置新密码
  const renderStepTwo = () => (
    <>
      <Form.Item
        name="password"
        rules={[
          { required: true, message: languageManager.t('auth.enterPassword') },
          { min: 6, max: 32, message: languageManager.t('auth.passwordLength') }
        ]}
        hasFeedback
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder={languageManager.t('auth.setNewPassword')}
          size="large"
          autoComplete="new-password"
          allowClear={false}
        />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        dependencies={['password']}
        hasFeedback
        rules={[
          { required: true, message: languageManager.t('auth.confirmPasswordRequired') },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(languageManager.t('auth.passwordsDoNotMatch')));
            },
          }),
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder={languageManager.t('auth.confirmPassword')}
          size="large"
          autoComplete="new-password"
          allowClear={false}
        />
      </Form.Item>

      <Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Button
              size="large"
              block
              onClick={prevStep}
              disabled={loading}
            >
              {languageManager.t('auth.prevStep')}
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              size="large"
              block
              onClick={nextStep}
              loading={loading}
            >
              {languageManager.t('auth.resetPassword')}
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </>
  );

  return (
    <Modal
      title={languageManager.t('auth.resetPassword')}
      open={visible}
      onCancel={handleClose}
      footer={null}
      maskClosable={false}
      centered
      className="reset-password-modal"
    >
      <Steps
        current={currentStep}
        items={[
          {
            title: languageManager.t('auth.verifyAccount'),
            description: languageManager.t('auth.verifyEmail'),
          },
          {
            title: languageManager.t('auth.setNewPassword'),
            description: languageManager.t('auth.setNewPasswordDesc'),
          },
        ]}
        className="reset-steps"
      />
      
      <Form
        form={form}
        name="resetPasswordForm"
        scrollToFirstError
        layout="vertical"
        className="reset-form"
      >
        {error && (
          <div className="reset-error-message">
            {error}
          </div>
        )}
        
        {currentStep === 0 ? renderStepOne() : renderStepTwo()}
        
        <div className="reset-form-footer">
          <Button type="link" onClick={handleGoToLogin}>
            {languageManager.t('auth.backToLogin')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ResetPasswordModal; 